import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Eye, Minus, Plus } from 'lucide-react';
import { Product } from '../data/products';
import { useCart } from '../context/CartContext';
import PurchaseModal from './PurchaseModal';

interface ProductCardProps {
  product: Product;
  variant?: 'default' | 'compact';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, variant = 'default' }) => {
  const { state, addToCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const isCompact = variant === 'compact';

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    // Modal commented out as requested
    // setShowPurchaseModal(true);
  };

  const handlePurchase = (platform: 'amazon' | 'flipkart') => {
    const url = platform === 'amazon' ? product.amazonLink : product.flipkartLink;
    if (url) {
      window.open(url, '_blank');
    }
    setShowPurchaseModal(false);
  };

  const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';

  return (
    <>
      <div
        className={`bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group overflow-hidden flex flex-col cursor-pointer ${
          isCompact ? 'h-full min-h-[430px] max-w-[280px]' : 'h-full max-h-full'
        }`}
        onClick={() => navigate(`/products/${product.id}`)}
      >
        <div className="relative flex-shrink-0">
            <div
              className="aspect-square w-full overflow-hidden bg-white flex items-center justify-center p-4 pt-6"
            >
              <img
                src={product.image || fallbackImage}
                alt={product.title}
                className="max-h-[85%] max-w-[85%] object-contain group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={(e) => {
                  if (e.currentTarget.src !== fallbackImage) {
                    e.currentTarget.src = fallbackImage;
                  }
                }}
              />
            </div>
          
          {/* Discount Badge */}
          {product.originalPrice && product.originalPrice > product.price && (
            <div 
              className="absolute top-2 left-2 text-white text-xs font-semibold px-2 py-1 rounded"
              style={{ backgroundColor: '#4554a4' }}
            >
              {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
            </div>
          )}

          {/* Quick Actions */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex flex-col space-y-2">
              {(product.sellOnSite ?? true) && (
                <button
                  onClick={handleAddToCart}
                  className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 transition-colors"
                  title="Add to Cart"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4554a4';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#4b5563';
                  }}
                >
                  <ShoppingCart className="w-4 h-4" />
                </button>
              )}
              <div
                className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 transition-colors"
                title="View Details"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#4554a4';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = '#4b5563';
                }}
              >
                <Eye className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        <div className={`${isCompact ? 'p-4' : 'p-4 sm:p-5'} flex flex-col flex-grow min-h-0`}>
          <div className={`mb-2 flex items-center flex-shrink-0 ${isCompact ? 'h-4' : 'h-4 sm:h-5'}`}>
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary-50 text-primary-600">
              {product.category}
            </span>
          </div>
          
          <div className="mb-1 flex-shrink-0">
            <h3 
              className={`font-bold text-gray-900 line-clamp-2 transition-colors leading-snug group-hover:text-primary-600 ${
                isCompact ? 'text-sm h-10' : 'text-base sm:text-lg h-12 sm:h-14'
              }`}
              title={product.title}
            >
              {product.title}
            </h3>
          </div>
          
          <p 
            className={`text-gray-600 line-clamp-2 flex-shrink-0 leading-tight ${
              isCompact ? 'text-xs h-7' : 'text-xs sm:text-sm mb-1 h-8 sm:h-9'
            }`}
            title={product.shortDescription || product.description}
          >
            {product.shortDescription || product.description}
          </p>

          {/* Rating */}
          <div className={`${isCompact ? 'h-4 mb-1' : 'h-4 sm:h-5 mb-1'} flex items-center flex-shrink-0`}>
            {product.rating ? (
              <div className="flex items-center">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        i < Math.floor(product.rating!)
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs sm:text-sm text-gray-500 ml-1 sm:ml-2">
                  {product.rating} ({product.reviews})
                </span>
              </div>
            ) : (
              <div></div>
            )}
          </div>

          {/* Color Variant Dots */}
          {product.colorVariants && product.colorVariants.length > 0 && (
            <div className="flex items-center space-x-1.5 my-1 flex-shrink-0">
              {product.colorVariants.slice(0, 4).map((variant, idx) => (
                <div
                  key={idx}
                  className="w-3.5 h-3.5 rounded-full border border-gray-300 shadow-inner"
                  style={{ backgroundColor: variant.colorCode || '#4554a4' }}
                  title={variant.name}
                />
              ))}
              {product.colorVariants.length > 4 && (
                <span className="text-[10px] text-gray-500 font-semibold pl-0.5">
                  +{product.colorVariants.length - 4} colors
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className={`flex items-center justify-between ${isCompact ? 'mb-2 h-7' : 'mb-2 sm:mb-3 h-7 sm:h-8'} flex-shrink-0`}>
            <div className="flex items-center space-x-2">
              <span className={`${isCompact ? 'text-base' : 'text-lg sm:text-xl'} font-bold text-gray-900`}>₹{product.price}</span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className={`${isCompact ? 'text-xs' : 'text-xs sm:text-sm'} text-gray-500 line-through`}>₹{product.originalPrice}</span>
              )}
            </div>
          </div>

          {/* Action Buttons - Pushed to bottom */}
          <div className={`mt-auto flex flex-col ${isCompact ? 'gap-2' : 'sm:flex-row gap-2'} flex-shrink-0 pt-3 border-t border-gray-50`}>
            {(product.sellOnSite ?? true) ? (() => {
              const cartItem = state.items.find(item => item.product.id === product.id);
              
              if (cartItem) {
                return (
                  <div className={`flex-1 flex items-center justify-between bg-gray-100 rounded-xl px-1 ${isCompact ? 'py-1 min-h-[38px]' : 'py-1.5 sm:py-2 min-h-[40px] sm:min-h-[44px]'}`}>
                    <div className="flex items-center space-x-2 bg-white rounded-lg shadow-sm px-2 py-1">
                      <button 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(product.id, cartItem.quantity - 1); }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="text-xs sm:text-sm font-medium w-4 text-center text-gray-900">{cartItem.quantity}</span>
                      <button 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(product.id, cartItem.quantity + 1); }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/checkout'); }}
                      className="text-xs sm:text-sm font-bold text-primary-600 hover:text-primary-700 px-2 flex-1 text-center truncate"
                    >
                      View Cart
                    </button>
                  </div>
                );
              }

              return (
              <button
                onClick={handleAddToCart}
                className={`flex-1 text-white rounded-xl font-bold text-xs transition-all flex items-center justify-center whitespace-nowrap shadow-sm hover:shadow-md ${
                  isCompact ? 'py-2.5 px-2 min-h-[38px]' : 'py-2.5 sm:py-3 px-3 sm:px-4 min-h-[40px] sm:min-h-[44px]'
                }`}
                style={{ backgroundColor: '#111827' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
              >
                <ShoppingCart className="w-4 h-4 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="truncate text-xs sm:text-sm">Add to Cart</span>
              </button>
              );
            })() : (
              <>
                {product.amazonLink && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePurchase('amazon'); }}
                    className={`flex-1 text-white bg-gray-900 rounded-xl font-bold text-xs transition-all flex items-center justify-center whitespace-nowrap shadow-sm hover:shadow-md hover:bg-gray-800 ${
                      isCompact ? 'py-2.5 px-2 min-h-[38px]' : 'py-2.5 sm:py-3 px-3 sm:px-4 min-h-[40px] sm:min-h-[44px]'
                    }`}
                  >
                    Amazon
                  </button>
                )}
                {product.flipkartLink && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePurchase('flipkart'); }}
                    className={`flex-1 text-white bg-blue-600 rounded-xl font-bold text-xs transition-all flex items-center justify-center whitespace-nowrap shadow-sm hover:shadow-md hover:bg-blue-700 ${
                      isCompact ? 'py-2.5 px-2 min-h-[38px]' : 'py-2.5 sm:py-3 px-3 sm:px-4 min-h-[40px] sm:min-h-[44px]'
                    }`}
                  >
                    Flipkart
                  </button>
                )}
                {(!product.amazonLink && !product.flipkartLink) && (
                   <span className={`flex-1 flex items-center justify-center text-gray-500 text-xs sm:text-sm ${isCompact ? 'py-2.5' : 'py-2.5 sm:py-3'}`}>Unavailable</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        product={product}
        onPurchase={handlePurchase}
      />
    </>
  );
};

export default ProductCard;
